/**
 * In-panel assistant tabs and page chips.
 * Nothing here is a nav item. Explain and Report stay stubs until later phases.
 */

export const ASSISTANT_TABS = [
  { id: 'ask', label: 'Ask' },
  { id: 'guides', label: 'Guides' },
  { id: 'explain', label: 'Explain' },
  { id: 'report', label: 'Report / Suggest' },
];

export const EXPLAIN_STUB =
  'Explainers are coming soon. What a figure means, and how it is calculated, will be answered from the reviewed catalog. This panel does not invent a formula.';

export const REPORT_STUB =
  'Sending a problem report or an idea to the D2D team is coming soon. Nothing is filed from this panel yet.';

/** Key figure named in the explain chips for each dashboardKey. */
export const PAGE_METRICS = {
  deals: 'the deal list',
  projects: 'the deal list',
  screen: 'the screen outcome',
  analysis: 'the screen outcome',
  forecast: "the seller's forecast case",
  value: 'IRR at bid',
  finance: 'the maximum note',
  abs: 'the illustrative maximum price',
  package: 'the frozen case',
  checklist: 'readiness',
  diligence: 'readiness',
  dataroom: 'section status',
  assets: 'EUR',
  well: 'seller PV10',
  reserves: 'PDP PV10',
  cashflow: 'L12 NOCF',
  wells: 'largest-well share',
  decision: 'IRR at bid',
  executive: 'PDP PV10',
  sensitivity: "the seller's forecast case",
  admin: 'a role',
  crm: 'the selected firm',
  map: 'the well map',
  land: 'held-by-production acreage',
  geoscience: 'the log inventory',
  overview: 'the screen outcome',
};

export function metricForPage(dashboardKey) {
  return PAGE_METRICS[dashboardKey] || 'this figure';
}

/**
 * Two deal questions from the existing list, plus guide, meaning, calculation,
 * and problem chips. Meaning and calculation open the Explain stub.
 * The problem chip opens the Report stub. Neither calls the model.
 */
export function chipsForPage(dashboardKey, suggestions) {
  const metric = metricForPage(dashboardKey);
  const dealQuestions = (suggestions || []).filter((item) => typeof item === 'string' && item.trim()).slice(0, 2);
  return [
    ...dealQuestions.map((text) => ({
      id: `deal:${text}`,
      label: text,
      text,
      tab: 'ask',
      intent: 'ask',
    })),
    {
      id: 'how',
      label: 'How does this page work?',
      text: 'How does this page work?',
      tab: 'guides',
      intent: 'how_it_works',
    },
    {
      id: 'mean',
      label: `What does ${metric} mean?`,
      text: `What does ${metric} mean?`,
      tab: 'explain',
      intent: 'what_it_means',
    },
    {
      id: 'calc',
      label: `How is ${metric} calculated?`,
      text: `How is ${metric} calculated?`,
      tab: 'explain',
      intent: 'how_calculated',
    },
    {
      id: 'report',
      label: 'Report a problem on this page',
      text: 'Report a problem on this page',
      tab: 'report',
      intent: 'feedback_bug',
    },
  ];
}

function pageCount(guide) {
  if (Array.isArray(guide.pages) && guide.pages.length) return guide.pages.length;
  return guide.page ? 1 : 99;
}

/**
 * Prefer the narrowest guide whose primary page is this dashboard.
 * A panel tour filed on another page stays out of that slot.
 */
export function guideForPage(guides, dashboardKey) {
  const list = (Array.isArray(guides) ? guides : []).filter((guide) => guide && guide.id);
  const exact = list.filter((guide) => guide.page === dashboardKey);
  const pool = exact.length
    ? exact
    : list.filter((guide) => Array.isArray(guide.pages) && guide.pages.includes(dashboardKey));
  if (!pool.length) return null;
  return [...pool].sort(
    (a, b) => pageCount(a) - pageCount(b) || String(a.title || '').localeCompare(String(b.title || '')),
  )[0];
}

/** Ask and Guides keep separate transcripts. Missing channel is an older Ask turn. */
export function channelFromStored(value) {
  return value === 'guides' ? 'guides' : 'ask';
}

/** Server route wins over the tab the question was typed on. */
export function laneFromServer(tab, fallback) {
  if (tab === 'guides' || tab === 'ask') return tab;
  return fallback === 'guides' ? 'guides' : 'ask';
}

/**
 * A question typed on Ask stays on Ask, even when the server calls it a guide.
 * A guide the user picked follows the server lane.
 */
export function laneForComposerTurn({ intent, serverLane }) {
  if (intent === 'ask') return 'ask';
  if (serverLane === 'guides' || serverLane === 'ask') return serverLane;
  return 'ask';
}

/** Retag the latest user turn and the replies after it once the server lane is known. */
export function applyServerLane(messages, lane) {
  const next = (Array.isArray(messages) ? messages : []).map((message) => ({ ...message }));
  let userIndex = -1;
  for (let index = next.length - 1; index >= 0; index -= 1) {
    if (next[index]?.role === 'user') {
      userIndex = index;
      break;
    }
  }
  if (userIndex < 0) return next;
  const settled = lane === 'guides' ? 'guides' : 'ask';
  for (let index = userIndex; index < next.length; index += 1) {
    next[index] = { ...next[index], channel: settled };
  }
  return next;
}

/** Ask stays on deal Q&A. Guides is the only tab that sends a guide intent. */
export function intentForTab(tab, explicitIntent) {
  if (explicitIntent) return explicitIntent;
  return tab === 'guides' ? 'how_it_works' : 'ask';
}

/**
 * A tab click while a turn is streaming detaches that turn.
 * The next question uses the tab the user just selected.
 */
export const STOPPED_ASK_AGAIN = 'Stopped. Ask again.';

/**
 * A tab switch aborted the stream. Keep the question and leave a visible reply
 * (any partial text, then the stopped note) so the turn is not silent.
 */
export function stoppedTurnAfterDetach({ question, partial = '' } = {}) {
  const streamed = String(partial || '').trim();
  const content = streamed ? `${streamed}\n\n${STOPPED_ASK_AGAIN}` : STOPPED_ASK_AGAIN;
  const retryQuestion = String(question || '').trim();
  return {
    role: 'assistant',
    content,
    meta: 'stopped',
    retryQuestion: retryQuestion || null,
  };
}

export function nextTurnAfterTabSwitch({ inFlight, nextTab }) {
  return {
    detachStream: Boolean(inFlight),
    tab: nextTab,
    intent: intentForTab(nextTab),
  };
}

/** The stream may move the visible tab only until the user picks one. */
export function tabAfterServerLane({ pinned, currentTab, serverLane }) {
  if (pinned) return currentTab === 'guides' ? 'guides' : currentTab || 'ask';
  if (serverLane === 'guides' || serverLane === 'ask') return serverLane;
  return currentTab === 'guides' ? 'guides' : 'ask';
}

const GUIDE_TRAILER = /\n+Guide: (trn-[a-z0-9-]{1,80})\s*$/;

/** The panel renders the trailer as an Open this guide control, not as raw text. */
export function splitGuideLink(content) {
  const text = typeof content === 'string' ? content : '';
  const match = text.match(GUIDE_TRAILER);
  if (!match) return { text, guideId: null };
  return { text: text.slice(0, match.index).trimEnd(), guideId: match[1] };
}

/** Deal questions must not resend guide replies, and guide questions stay on Guides. */
export function historyForIntent(messages, intent) {
  const lane = intent === 'how_it_works' ? 'guides' : 'ask';
  return (Array.isArray(messages) ? messages : [])
    .filter((message) => message && (message.channel || 'ask') === lane)
    .map((message) => ({
      role: message.role,
      content: message.content,
      channel: message.channel || 'ask',
    }));
}
